import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { Home, ArrowRight, ArrowLeft, Download, Shield, FileText, Lightbulb, CheckCircle, Banknote, Users, Building, ThermometerSun, PoundSterling, FileCheck, Share2, Mail, ThumbsUp, ThumbsDown, Send } from "lucide-react";
import AnonymityBanner from "@/components/AnonymityBanner";
import NextStepsFooter from "@/components/NextStepsFooter";
import TransitionReassurance from "@/components/TransitionReassurance";
import "@/styles/govtech.css";

// Question Flow for Under-Occupation & Exchange Triage
const QUESTIONS = [
  {
    id: "current_bedrooms",
    step: 1,
    question: "How many bedrooms does your current property have?",
    type: "select",
    options: [
      { value: "1", label: "1 Bedroom" },
      { value: "2", label: "2 Bedrooms" },
      { value: "3", label: "3 Bedrooms" },
      { value: "4", label: "4 Bedrooms" },
      { value: "5", label: "5+ Bedrooms" }
    ],
    insight: null
  },
  {
    id: "household_size",
    step: 2,
    question: "How many people live in your household?",
    type: "select",
    options: [
      { value: "1", label: "1 person (just me)" },
      { value: "2", label: "2 people" },
      { value: "3", label: "3 people" },
      { value: "4", label: "4 people" },
      { value: "5", label: "5+ people" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Under-occupation is assessed based on the 'Bedroom Standard.' Generally, you're entitled to one bedroom per couple, one for each adult 21+, one per pair of children under 10, and one per pair of same-sex children 10-20."
    }
  },
  {
    id: "desired_bedrooms",
    step: 3,
    question: "How many bedrooms do you actually need?",
    type: "select",
    options: [
      { value: "1", label: "1 Bedroom" },
      { value: "2", label: "2 Bedrooms" },
      { value: "3", label: "3 Bedrooms" },
      { value: "4", label: "4+ Bedrooms" }
    ],
    insight: null
  },
  {
    id: "reason_for_move",
    step: 4,
    question: "What is your primary reason for considering a move?",
    type: "select",
    options: [
      { value: "reduce_bills", label: "Reduce heating and utility bills" },
      { value: "easier_manage", label: "Property is too large to manage/maintain" },
      { value: "health_mobility", label: "Health or mobility reasons" },
      { value: "financial", label: "Financial pressures / reduce rent" },
      { value: "location", label: "Want to move to a different area" },
      { value: "family_change", label: "Family circumstances have changed" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Councils prioritise moves that free up larger homes. If you're in a 3-bed and only need 1-bed, you may qualify for priority transfer status and financial incentives."
    }
  },
  {
    id: "property_type",
    step: 5,
    question: "What type of property do you currently live in?",
    type: "select",
    options: [
      { value: "house", label: "House" },
      { value: "flat_ground", label: "Flat - Ground Floor" },
      { value: "flat_upper", label: "Flat - Upper Floor (with lift)" },
      { value: "flat_no_lift", label: "Flat - Upper Floor (no lift)" },
      { value: "maisonette", label: "Maisonette" },
      { value: "bungalow", label: "Bungalow" }
    ],
    insight: null
  },
  {
    id: "arrears_status",
    step: 6,
    question: "Are you currently up to date with your rent payments?",
    type: "select",
    options: [
      { value: "clear", label: "Yes, my rent account is clear" },
      { value: "arrangement", label: "I have arrears but am on a repayment plan" },
      { value: "arrears", label: "I have rent arrears" }
    ],
    insight: {
      type: "pro-tip",
      title: "10-Year Insight",
      content: "Most transfer schemes require a clear rent account or an active repayment agreement. Being on a payment plan doesn't disqualify you—it shows responsibility."
    }
  }
];

// Logic Engine for calculating results
const calculateResult = (answers) => {
  const currentBeds = parseInt(answers.current_bedrooms) || 0;
  const desiredBeds = parseInt(answers.desired_bedrooms) || 0;
  const householdSize = parseInt(answers.household_size) || 1;
  
  const bedroomDifference = currentBeds - desiredBeds;
  const isUnderOccupying = currentBeds > householdSize;
  
  // Calculate potential savings (estimates based on UK averages)
  const heatingPerBedroom = 45; // £45/month per bedroom
  const councilTaxSaving = householdSize === 1 ? 75 : 0; // Single person discount
  const potentialRentReduction = bedroomDifference * 85; // £85/month per bedroom
  
  const monthlySavings = (bedroomDifference * heatingPerBedroom) + councilTaxSaving + potentialRentReduction;
  const annualSavings = monthlySavings * 12;
  
  // Determine eligibility status
  let status, title, message, priority;
  
  if (answers.arrears_status === "arrears") {
    status = "amber";
    title = "Address Rent Arrears First";
    message = "Most transfer and exchange schemes require a clear rent account. Contact your Housing Officer to discuss a repayment arrangement before applying.";
    priority = "Standard";
  } else if (bedroomDifference >= 2) {
    status = "green";
    title = "High Priority for Transfer";
    message = `You are significantly under-occupying your home. Freeing up a ${currentBeds}-bedroom property could qualify you for priority transfer status and financial incentives.`;
    priority = "High Priority";
  } else if (bedroomDifference === 1) {
    status = "green";
    title = "Downsizing Options May Suit You";
    message = "You may benefit from moving to a smaller property. Many Councils offer Downsizing Grants and priority transfer for residents willing to free up larger homes.";
    priority = "Medium Priority";
  } else if (isUnderOccupying) {
    status = "amber";
    title = "Potential Under-Occupation";
    message = "Based on your household size, you may have more space than you need. Consider exploring your options for a more suitable property.";
    priority = "Standard";
  } else {
    status = "amber";
    title = "Mutual Exchange May Be Your Best Option";
    message = "You don't appear to be under-occupying, but if you want to move, Mutual Exchange with another tenant could be your pathway.";
    priority = "Standard";
  }
  
  return {
    status,
    title,
    message,
    priority,
    currentBeds,
    desiredBeds,
    bedroomDifference,
    monthlySavings,
    annualSavings,
    isUnderOccupying,
    heatingPerBedroom,
    councilTaxSaving,
    potentialRentReduction
  };
};

// Generate Comprehensive PDF
const generatePDF = (answers, result) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let yPos = 20;
  
  const addWrappedText = (text, x, y, maxWidth, lineHeight = 7) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, x, y);
    return y + (lines.length * lineHeight);
  };
  
  // Header
  doc.setFillColor(0, 112, 60);
  doc.rect(0, 0, pageWidth, 40, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont("helvetica", "bold");
  doc.text("TenURes.co.uk", margin, 25);
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text("YOUR LIVEABILITY GUIDANCE SUMMARY", margin, 35);
  
  yPos = 55;
  
  // Authority Stamp
  doc.setFillColor(244, 244, 244);
  doc.rect(margin, yPos, contentWidth, 25, 'F');
  doc.setDrawColor(0, 112, 60);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 25);
  
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("OFFICER-READY EVIDENCE PACK", margin + 5, yPos + 10);
  doc.setFont("helvetica", "normal");
  doc.text("Verified with 10 Years of Frontline Housing Management Experience", margin + 5, yPos + 18);
  
  yPos += 35;
  
  // Priority Status
  const statusColors = {
    green: { bg: [204, 226, 216], text: [0, 112, 60] },
    amber: { bg: [252, 214, 195], text: [110, 54, 25] }
  };
  
  const statusStyle = statusColors[result.status];
  doc.setFillColor(...statusStyle.bg);
  doc.rect(margin, yPos, contentWidth, 15, 'F');
  doc.setDrawColor(...statusStyle.text);
  doc.setLineWidth(1);
  doc.line(margin, yPos, margin, yPos + 15);
  
  doc.setTextColor(...statusStyle.text);
  doc.setFontSize(11);
  doc.setFont("helvetica", "bold");
  doc.text(`TRANSFER PRIORITY: ${result.priority.toUpperCase()}`, margin + 5, yPos + 10);
  
  yPos += 25;
  
  // Result
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  yPos = addWrappedText(result.title, margin, yPos, contentWidth, 8);
  
  yPos += 5;
  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  yPos = addWrappedText(result.message, margin, yPos, contentWidth);
  
  yPos += 15;
  
  // Property Prospectus Section
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Property Prospectus", margin, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const propertyType = answers.property_type?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified';
  const propertyDetails = [
    `Property Type: ${propertyType}`,
    `Current Bedrooms: ${answers.current_bedrooms}`,
    `Household Size: ${answers.household_size} person(s)`,
    `Reason for Move: ${answers.reason_for_move?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Not specified'}`
  ];
  
  propertyDetails.forEach(detail => {
    doc.text(detail, margin, yPos);
    yPos += 7;
  });
  
  yPos += 10;
  
  // Financial Impact Statement
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Financial Impact Statement", margin, yPos);
  yPos += 10;
  
  doc.setFillColor(240, 253, 244);
  doc.rect(margin, yPos, contentWidth, 50, 'F');
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(51, 51, 51);
  
  if (result.bedroomDifference > 0) {
    doc.text(`By moving from a ${result.currentBeds}-bed to a ${result.desiredBeds}-bed property:`, margin + 5, yPos + 10);
    doc.text(`• Estimated heating savings: £${result.bedroomDifference * result.heatingPerBedroom}/month`, margin + 5, yPos + 20);
    if (result.councilTaxSaving > 0) {
      doc.text(`• Council Tax single person discount: £${result.councilTaxSaving}/month`, margin + 5, yPos + 28);
    }
    doc.text(`• Potential rent reduction: £${result.potentialRentReduction}/month`, margin + 5, yPos + 36);
    
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 112, 60);
    doc.text(`TOTAL POTENTIAL ANNUAL SAVINGS: £${result.annualSavings}`, margin + 5, yPos + 46);
  } else {
    doc.text("No significant financial benefit from downsizing identified.", margin + 5, yPos + 20);
    doc.text("Mutual Exchange may still offer lifestyle benefits.", margin + 5, yPos + 30);
  }
  
  yPos += 60;
  
  // Verification Statement
  doc.setTextColor(51, 51, 51);
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Verification Statement", margin, yPos);
  yPos += 10;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const rentStatus = answers.arrears_status === "clear" 
    ? "✓ Rent account reported as CLEAR" 
    : answers.arrears_status === "arrangement"
    ? "○ Rent account: On active repayment arrangement"
    : "✗ Rent arrears reported - address before transfer";
  
  doc.text(rentStatus, margin, yPos);
  yPos += 7;
  doc.text("✓ Intent to Move: CONFIRMED", margin, yPos);
  yPos += 7;
  doc.text(`✓ Summary Date: ${new Date().toLocaleDateString('en-GB')}`, margin, yPos);
  
  // New page for Checklist
  doc.addPage();
  yPos = 20;
  
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text("Downsizing & Transfer Checklist", margin, yPos);
  yPos += 15;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const checklist = [
    "Contact your Housing Officer to express interest in downsizing",
    "Request information about Transfer Incentive Schemes in your Borough",
    "Gather 3 months of bank statements showing rent payments",
    "Obtain a reference letter from your current landlord",
    "Take photos of your property for exchange listing",
    "Use the TenURes Exchange Matcher to find compatible swap partners",
    "Complete a Housing Register application if not already registered",
    "Attend any required housing assessment appointments",
    "Review and respond to any exchange offers promptly"
  ];
  
  checklist.forEach((item, index) => {
    doc.rect(margin, yPos - 3, 4, 4);
    doc.text(`${index + 1}. ${item}`, margin + 8, yPos);
    yPos += 10;
  });
  
  yPos += 10;
  
  // Cover Letter Template
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Template Letter to Housing Officer", margin, yPos);
  yPos += 15;
  
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  
  const letterContent = `Dear Housing Officer,

RE: Expression of Interest - Downsizing/Transfer

I am writing to formally express my interest in downsizing from my current ${answers.current_bedrooms}-bedroom ${propertyType.toLowerCase()} to a smaller property.

Current situation:
- Household size: ${answers.household_size} person(s)
- Bedrooms needed: ${answers.desired_bedrooms}
- Primary reason: ${answers.reason_for_move?.replace('_', ' ') || 'Personal circumstances'}

I understand this would free up a ${answers.current_bedrooms}-bedroom property for a family in greater need, and I am keen to support the Council's housing objectives.

I would be grateful for information about:
1. Any Downsizing Grant or financial incentive available
2. Priority banding for voluntary transfers
3. Mutual Exchange support and assistance

I am happy to attend any appointments and provide additional documentation.

Yours faithfully,

[Your Name]
[Your Address]`;

  const letterLines = doc.splitTextToSize(letterContent, contentWidth);
  doc.text(letterLines, margin, yPos);
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(
      `Generated by TenURes.co.uk | ${new Date().toLocaleDateString('en-GB')} | Page ${i} of ${pageCount}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
    doc.text(
      "This is a guidance document. Final transfer decisions are made by your Housing Provider.",
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 5,
      { align: 'center' }
    );
  }
  
  doc.save("TenURes_Liveability_Guidance_Summary.pdf");
};

// Tenures Logo
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

// Status Badge
const StatusBadge = ({ status, priority }) => {
  const config = {
    green: { icon: CheckCircle, className: "status-green" },
    amber: { icon: FileCheck, className: "status-amber" }
  };
  
  const { icon: Icon, className } = config[status];
  
  return (
    <div className={`status-badge ${className}`}>
      <Icon size={18} />
      <span>{priority}</span>
    </div>
  );
};

// Savings Card
const SavingsCard = ({ icon: Icon, label, amount, period }) => (
  <div className="bg-white border border-gray-200 p-4 text-center">
    <Icon className="text-[#00703C] mx-auto mb-2" size={24} />
    <p className="text-xs text-gray-500 mb-1">{label}</p>
    <p className="text-xl font-bold text-[#00703C]">£{amount}</p>
    <p className="text-xs text-gray-400">{period}</p>
  </div>
);

export default function LiveabilityTriage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  
  // Email capture state
  const [emailCapture, setEmailCapture] = useState({ email: "", postcode: "" });
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Feedback state
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  
  const currentQuestion = QUESTIONS[currentStep];
  const progress = ((currentStep + 1) / QUESTIONS.length) * 100;
  
  const handleAnswer = (value) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };
  
  const handleNext = () => {
    if (!answers[currentQuestion.id]) return;
    
    if (currentStep < QUESTIONS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      const triageResult = calculateResult(answers);
      setResult(triageResult);
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };
  
  const handleDownloadPDF = () => {
    generatePDF(answers, result);
    setPdfDownloaded(true);
  };
  
  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (!emailCapture.email) return;
    
    const leadData = {
      email: emailCapture.email,
      postcode: emailCapture.postcode,
      tool: "liveability",
      savings: result?.monthlySavings,
      timestamp: new Date().toISOString()
    };
    
    const existingLeads = JSON.parse(localStorage.getItem('tenures_resident_leads') || '[]');
    existingLeads.push(leadData);
    localStorage.setItem('tenures_resident_leads', JSON.stringify(existingLeads));
    
    console.log(`[Lead Captured] Liveability Triage:`, leadData);
    setEmailSent(true);
    generatePDF(answers, result);
    setPdfDownloaded(true);
  };
  
  const handleFeedback = (isPositive) => {
    const feedbackData = {
      tool: "liveability",
      helpful: isPositive,
      timestamp: new Date().toISOString()
    };
    
    const existingFeedback = JSON.parse(localStorage.getItem('tenures_feedback') || '[]');
    existingFeedback.push(feedbackData);
    localStorage.setItem('tenures_feedback', JSON.stringify(existingFeedback));
    
    console.log(`[Feedback] Liveability Triage:`, feedbackData);
    setFeedbackGiven(true);
  };
  
  const handleRestart = () => {
    setCurrentStep(0);
    setAnswers({});
    setResult(null);
  };
  
  const handleShare = () => {
    const shareText = "I just used TenURes to check my downsizing options - it shows how much I could save by moving to a smaller place. Free tool for council tenants:";
    const shareUrl = window.location.origin + "/liveability-triage";
    
    if (navigator.share) {
      navigator.share({
        title: 'TenURes - Liveability & Downsizing Calculator',
        text: shareText,
        url: shareUrl
      });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/resident')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Portal</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Progress Bar */}
      {!result && (
        <div className="max-w-4xl mx-auto px-4 pt-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-500">Liveability Guidance Checker</span>
            <span className="text-sm text-gray-500">Step {currentStep + 1} of {QUESTIONS.length}</span>
          </div>
          <div className="progress-container">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="text-sm text-gray-500 text-center">About 2 minutes to complete</p>
          <div className="mt-4">
            <AnonymityBanner />
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 py-12">
        {/* Authority Stamp */}
        <div className="authority-stamp mb-8">
          <div className="authority-stamp-icon">
            <Shield size={20} />
          </div>
          <div>
            <p className="font-semibold text-gray-900">Designed with a Decade of Frontline Housing Expertise</p>
            <p className="text-sm text-gray-600">Evidence-based triage for downsizing and exchange</p>
          </div>
        </div>
        
        {!result ? (
          /* Question View */
          <div className="govtech-card">
            <TransitionReassurance step={currentStep} />
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center">
                <Home className="text-[#00703C]" size={20} />
              </div>
              <h1 className="govtech-heading-m">{currentQuestion.question}</h1>
            </div>
            
            {/* Pro Tip */}
            {currentQuestion.insight && (
              <div className="pro-tip mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Lightbulb size={16} className="text-[#F47738]" />
                  <span className="pro-tip-label">{currentQuestion.insight.title}</span>
                </div>
                <p className="text-gray-700 text-sm">{currentQuestion.insight.content}</p>
              </div>
            )}
            
            {/* Options */}
            <div className="govtech-radio-group mb-8">
              {currentQuestion.options.map((option) => (
                <div
                  key={option.value}
                  onClick={() => handleAnswer(option.value)}
                  className={`govtech-radio-item ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                  data-testid={`option-${option.value}`}
                >
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                    answers[currentQuestion.id] === option.value 
                      ? 'border-[#00703C] bg-[#00703C]' 
                      : 'border-gray-400'
                  }`}>
                    {answers[currentQuestion.id] === option.value && (
                      <div className="w-2 h-2 rounded-full bg-white" />
                    )}
                  </div>
                  <span className="text-gray-900">{option.label}</span>
                </div>
              ))}
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex gap-4">
              {currentStep > 0 && (
                <button
                  onClick={handleBack}
                  className="govtech-btn govtech-btn-secondary flex-1"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!answers[currentQuestion.id]}
                className={`govtech-btn govtech-btn-primary flex-1 ${!answers[currentQuestion.id] ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {currentStep < QUESTIONS.length - 1 ? (
                  <>
                    Continue
                    <ArrowRight size={18} className="ml-2" />
                  </>
                ) : (
                  "See My Guidance Summary"
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Result View */
          <div className="space-y-6">
            <div className="govtech-card">
              <AnonymityBanner variant="full" />
              <div className="text-center mb-6">
                <h1 className="govtech-heading-m mb-4">Your Liveability Guidance Summary</h1>
                <StatusBadge status={result.status} priority={result.priority} />
              </div>

              <NextStepsFooter reviewDays={28} teamName="lettings team" />
              
              <div className="govtech-card govtech-card-highlight mb-6">
                <h2 className="font-bold text-gray-900 mb-2">{result.title}</h2>
                <p className="text-gray-700">{result.message}</p>
              </div>
              
              {/* Financial Savings */}
              {result.bedroomDifference > 0 && (
                <div className="mb-6">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <PoundSterling className="text-[#00703C]" size={20} />
                    Potential Financial Savings
                  </h3>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <SavingsCard 
                      icon={ThermometerSun}
                      label="Heating Savings"
                      amount={result.bedroomDifference * result.heatingPerBedroom}
                      period="per month"
                    />
                    <SavingsCard 
                      icon={Building}
                      label="Rent Reduction"
                      amount={result.potentialRentReduction}
                      period="per month"
                    />
                    <SavingsCard 
                      icon={Banknote}
                      label="Annual Total"
                      amount={result.annualSavings}
                      period="per year"
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center">
                    * Estimates based on UK averages. Actual savings may vary by location and provider.
                  </p>
                </div>
              )}
              
              {/* What's in the PDF */}
              <div className="bg-gray-50 p-4 mb-6">
                <h3 className="font-bold text-gray-900 mb-3">Your Comprehensive Evidence Pack includes:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Property Prospectus</strong> — Structured description for exchange partners</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Financial Impact Statement</strong> — Breakdown of potential savings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Verification Stamp</strong> — 10-Year frontline-vetted eligibility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Downsizing Checklist</strong> — Step-by-step transfer guide</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="text-[#00703C] mt-0.5" size={16} />
                    <span><strong>Cover Letter Template</strong> — Professional letter for your Housing Officer</span>
                  </li>
                </ul>
              </div>
              
              {/* Exchange Matcher CTA */}
              <div className="bg-[#00703C] bg-opacity-5 border border-[#00703C] border-opacity-30 p-4 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#00703C] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <Users className="text-[#00703C]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#333333] mb-1">Find Exchange Partners</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Use our Exchange Matcher to find other residents looking to swap. We'll match you based on what you have and what you need.
                    </p>
                    <button 
                      onClick={() => navigate('/exchange-matcher')}
                      className="text-[#00703C] font-medium text-sm flex items-center gap-1 hover:underline"
                    >
                      Go to Exchange Matcher <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-col gap-3">
                {/* Email Capture Option */}
                {!showEmailForm && !emailSent && (
                  <button
                    onClick={() => setShowEmailForm(true)}
                    className="govtech-btn govtech-btn-primary w-full"
                    data-testid="email-pdf-btn"
                  >
                    <Mail size={18} className="mr-2" />
                    Email Me My Report
                  </button>
                )}
                
                {showEmailForm && !emailSent && (
                  <form onSubmit={handleEmailSubmit} className="bg-gray-50 p-4 space-y-3">
                    <p className="text-sm font-medium text-[#333333]">Get your report sent to your inbox:</p>
                    <input
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="govtech-input"
                      value={emailCapture.email}
                      onChange={(e) => setEmailCapture({...emailCapture, email: e.target.value})}
                    />
                    <input
                      type="text"
                      placeholder="Your postcode (optional)"
                      className="govtech-input"
                      value={emailCapture.postcode}
                      onChange={(e) => setEmailCapture({...emailCapture, postcode: e.target.value})}
                    />
                    <div className="flex gap-2">
                      <button type="submit" className="govtech-btn govtech-btn-primary flex-1">
                        <Send size={16} className="mr-2" />
                        Send & Download
                      </button>
                      <button 
                        type="button" 
                        onClick={() => setShowEmailForm(false)}
                        className="govtech-btn govtech-btn-secondary"
                      >
                        Cancel
                      </button>
                    </div>
                    <p className="text-xs text-gray-500">We'll only email you your report. No spam, ever.</p>
                  </form>
                )}
                
                {emailSent && (
                  <div className="bg-[#00703C] bg-opacity-10 border border-[#00703C] p-4 text-center">
                    <CheckCircle className="text-[#00703C] mx-auto mb-2" size={24} />
                    <p className="text-[#00703C] font-medium">Report sent to {emailCapture.email}</p>
                  </div>
                )}
                
                {!showEmailForm && (
                  <button
                    onClick={handleDownloadPDF}
                    className="govtech-btn govtech-btn-secondary w-full"
                    data-testid="download-pdf-btn"
                  >
                    <Download size={18} className="mr-2" />
                    {emailSent ? 'Download Again' : 'Just Download PDF'}
                  </button>
                )}
                
                {/* Feedback Section */}
                {pdfDownloaded && !feedbackGiven && (
                  <div className="bg-gray-50 p-4 mt-2">
                    <p className="text-sm font-medium text-[#333333] text-center mb-3">Was this tool helpful?</p>
                    <div className="flex justify-center gap-4">
                      <button
                        onClick={() => handleFeedback(true)}
                        className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-[#00703C] hover:bg-[#00703C] hover:bg-opacity-5 transition-all"
                      >
                        <ThumbsUp size={18} className="text-[#00703C]" />
                        <span>Yes</span>
                      </button>
                      <button
                        onClick={() => handleFeedback(false)}
                        className="flex items-center gap-2 px-6 py-2 bg-white border border-gray-200 hover:border-red-400 hover:bg-red-50 transition-all"
                      >
                        <ThumbsDown size={18} className="text-gray-500" />
                        <span>No</span>
                      </button>
                    </div>
                  </div>
                )}
                
                {feedbackGiven && (
                  <div className="bg-[#00703C] bg-opacity-5 p-3 text-center">
                    <p className="text-sm text-[#00703C]">Thank you for your feedback!</p>
                  </div>
                )}
                
                <button
                  onClick={handleShare}
                  className="govtech-btn govtech-btn-secondary w-full"
                  data-testid="share-btn"
                >
                  <Share2 size={18} className="mr-2" />
                  Share With a Neighbour
                </button>
                
                <button
                  onClick={handleRestart}
                  className="text-[#00703C] font-medium text-sm hover:underline text-center"
                >
                  Start a New Guidance Checker
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 mt-12">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm text-gray-500">
            This Guidance Checker is here to help you understand your housing options.
            Any transfer or exchange is confirmed by your landlord.
            Final decisions are made by your Housing Provider.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            © {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk — Professional Housing Infrastructure
          </p>
        </div>
      </footer>
    </div>
  );
}
